import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Loader2, Check, X, AlertCircle, Copy, Circle } from 'lucide-react';
import { isCefHost, getCefAppVersion } from '@/services/backend/cefUpdater';
import { useRecentActivityStore, ACTIVITY_WINDOW_MS } from '@/stores/recentActivityStore';
import { useProjectStore } from '@/stores/projectStore';
import { useTranscriptionQueueStore, type TranscriptionJob } from '@/stores/transcriptionQueueStore';
import { showToast } from '@/components/ui/Toast';

/**
 * Status bar at the bottom of the window. Combines:
 *  - Version info (left)
 *  - Activity light (right): green when ANY project has had Claude activity
 *    in the last 5 minutes AND the agent isn't sitting idle at the prompt.
 *    Click opens a popover listing those projects with a green dot — each
 *    item navigates to that project's workspace.
 */
export default function StatusFooter() {
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (isCefHost()) {
        const v = await getCefAppVersion();
        if (v) setVersion(v);
      }
      // Web mode: no version display (served from container)
    })();
  }, []);

  return (
    <div className="flex items-center h-6 px-3 bg-base-200 border-t border-base-300 text-[11px] text-base-content/60 select-none shrink-0 gap-4">
      {version && <span>AtelierCode v{version}</span>}
      <div className="flex-1" />
      <TranscriptionQueueIndicator />
      <ActivityIndicator />
    </div>
  );
}

/** Clickable badge for background voice transcriptions. The mic hands each
 *  recording off to the global queue so the next can start immediately; this
 *  surfaces live progress and, on click, a popover showing every job's stages
 *  (Transcribe → Enhance) with per-stage timers and check marks. Finished jobs
 *  linger a few minutes so results stay visible; errors linger longer and are
 *  click-to-copy so the message is easy to grab. */
function TranscriptionQueueIndicator() {
  const jobs = useTranscriptionQueueStore((s) => s.jobs);
  const dismiss = useTranscriptionQueueStore((s) => s.dismiss);
  const clearFinished = useTranscriptionQueueStore((s) => s.clearFinished);
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const list = Array.from(jobs.values());
  const active = list.filter((j) => j.stage === 'transcribing' || j.stage === 'enhancing');
  const errored = list.filter((j) => j.stage === 'error');

  // Live-timer tick: re-render ~2×/s while work is in flight or the popover is open.
  useEffect(() => {
    if (active.length === 0 && !open) return;
    const t = setInterval(() => setTick((x) => x + 1), 500);
    return () => clearInterval(t);
  }, [active.length, open]);

  // Click-outside to dismiss the popover.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(t) &&
        buttonRef.current && !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close automatically once everything has cleared.
  useEffect(() => {
    if (jobs.size === 0 && open) setOpen(false);
  }, [jobs.size, open]);

  if (jobs.size === 0) return null;

  const copyError = async (msg: string) => {
    try {
      await navigator.clipboard.writeText(msg);
      showToast('Error copied to clipboard', 'success');
    } catch {
      showToast('Could not copy — select the text to copy it', 'error');
    }
  };

  // Badge: amber+spinner while active; red if errors are lingering; else green "done".
  let badgeClass = 'text-emerald-300/90';
  let badgeIcon = <Check className="w-3 h-3" />;
  let badgeText = 'done';
  if (active.length > 0) {
    badgeClass = 'text-amber-300/90';
    badgeIcon = <Loader2 className="w-3 h-3 animate-spin" />;
    const enhancingOnly = active.every((j) => j.stage === 'enhancing');
    badgeText = active.length === 1
      ? (enhancingOnly ? 'enhancing' : 'transcribing')
      : `transcribing ${active.length}`;
  } else if (errored.length > 0) {
    badgeClass = 'text-red-400';
    badgeIcon = <AlertCircle className="w-3 h-3" />;
    badgeText = errored.length === 1 ? 'error' : `${errored.length} errors`;
  }

  const sorted = list.slice().sort((a, b) => a.startedAt - b.startedAt);
  const hasFinished = list.some((j) => j.stage === 'done' || j.stage === 'error');

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={`flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-base-300 cursor-pointer ${badgeClass}`}
        onClick={() => setOpen((v) => !v)}
        title="Voice transcriptions — click for details"
      >
        {badgeIcon}
        <span>{badgeText}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute bottom-full right-0 mb-2 w-80 max-h-96 overflow-y-auto rounded-md border border-base-300 bg-base-100 shadow-xl text-base-content z-50"
        >
          <div className="px-3 py-2 border-b border-base-300 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-base-content/50">Transcriptions</span>
            {hasFinished && (
              <button
                type="button"
                className="text-[10px] text-base-content/50 hover:text-base-content transition-colors"
                onClick={clearFinished}
              >
                Clear finished
              </button>
            )}
          </div>
          <ul className="py-1">
            {sorted.map((job) => (
              <TranscriptionJobRow key={job.id} job={job} onDismiss={dismiss} onCopyError={copyError} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type StepState = 'pending' | 'active' | 'done' | 'error';

function StepRow({ label, state, time }: { label: string; state: StepState; time: string }) {
  const icon =
    state === 'active' ? <Loader2 className="w-3 h-3 animate-spin text-amber-300" />
      : state === 'done' ? <Check className="w-3 h-3 text-emerald-400" />
        : state === 'error' ? <X className="w-3 h-3 text-red-400" />
          : <Circle className="w-3 h-3 text-base-content/25" />;
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {icon}
      <span className={state === 'pending' ? 'text-base-content/35' : 'text-base-content/70'}>{label}</span>
      {time && <span className="ml-auto tabular-nums text-base-content/45">{time}</span>}
    </div>
  );
}

function TranscriptionJobRow({
  job,
  onDismiss,
  onCopyError,
}: {
  job: TranscriptionJob;
  onDismiss: (id: number) => void;
  onCopyError: (msg: string) => void;
}) {
  const now = Date.now();
  const fmt = (ms: number) => `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
  // Transcribe spans until enhance began, or until completion, or "now" if live.
  const transcribeEnd = job.enhanceStartedAt ?? job.completedAt ?? now;
  const transcribeMs = transcribeEnd - job.startedAt;
  const enhanceMs = job.enhanceStartedAt ? (job.completedAt ?? now) - job.enhanceStartedAt : 0;

  const transcribeState: StepState =
    job.stage === 'transcribing' ? 'active'
      : (job.stage === 'error' && !job.enhanceStartedAt) ? 'error'
        : 'done';
  const enhanceState: StepState =
    job.stage === 'transcribing' ? 'pending'
      : job.stage === 'enhancing' ? 'active'
        : job.stage === 'error' ? 'error'
          : 'done';

  return (
    <li className="px-3 py-2 border-b border-base-300/40 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium truncate flex-1">{job.projectName || 'Transcription'}</span>
        {(job.stage === 'done' || job.stage === 'error') && (
          <button
            type="button"
            className="text-base-content/40 hover:text-base-content transition-colors"
            title="Dismiss"
            onClick={() => onDismiss(job.id)}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="mt-1 space-y-0.5">
        <StepRow label="Transcribe" state={transcribeState} time={fmt(transcribeMs)} />
        {job.enhanced && (
          <StepRow label="Enhance" state={enhanceState} time={job.enhanceStartedAt ? fmt(enhanceMs) : ''} />
        )}
      </div>
      {job.stage === 'error' && job.error && (
        <button
          type="button"
          onClick={() => onCopyError(job.error as string)}
          className="mt-1.5 w-full text-left flex items-start gap-1.5 text-[11px] text-red-400/90 hover:text-red-300 bg-red-500/5 hover:bg-red-500/10 rounded px-1.5 py-1 transition-colors"
          title="Click to copy the error"
        >
          <Copy className="w-3 h-3 mt-px shrink-0" />
          <span className="break-words select-text">{job.error}</span>
        </button>
      )}
      {job.stage === 'done' && job.preview && (
        <div className="mt-1 text-[11px] text-base-content/45 truncate">&ldquo;{job.preview}&rdquo;</div>
      )}
    </li>
  );
}

function ActivityIndicator() {
  const projects = useProjectStore((s) => s.projects);
  // Subscribe to the store's `activity` map AND tickCounter so this re-renders
  // both on real events and on the 30s sliding-window tick.
  const activity = useRecentActivityStore((s) => s.activity);
  const tick = useRecentActivityStore((s) => s.tickCounter);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // tick is intentionally referenced so the closure re-runs each tick — even
  // though the variable is unused, reading it subscribes us.
  void tick;

  // Compute "currently active" set inline (cheap — O(projects)).
  const now = Date.now();
  const activeByPath = new Set<string>();
  activity.forEach((a, projectPath) => {
    if (now - a.lastActivityAt > ACTIVITY_WINDOW_MS) return;
    if (a.lastActivityAt > a.lastCompletionAt) activeByPath.add(projectPath);
  });
  const activeProjects = projects.filter((p) => activeByPath.has(p.root_path));
  const anyActive = activeProjects.length > 0;

  // Click-outside to dismiss
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(t) &&
        buttonRef.current && !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dotColor = anyActive ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.7)]' : 'bg-stone-600';
  const label = anyActive
    ? `${activeProjects.length} active`
    : 'idle';

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="flex items-center gap-1.5 px-2 py-0.5 rounded hover:bg-base-300 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        title="Projects with activity in the last 5 minutes"
      >
        <Activity className="w-3 h-3 text-base-content/50" />
        <span className={`inline-block w-2 h-2 rounded-full transition-colors ${dotColor}`} />
        <span className="text-base-content/70">{label}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute bottom-full right-0 mb-2 w-72 max-h-80 overflow-y-auto rounded-md border border-base-300 bg-base-100 shadow-xl text-base-content z-50"
        >
          <div className="px-3 py-2 border-b border-base-300 text-[11px] uppercase tracking-wide text-base-content/50">
            Activity (last 5 min)
          </div>
          {activeProjects.length === 0 ? (
            <div className="px-3 py-3 text-xs text-base-content/50">
              No projects with active agents right now.
            </div>
          ) : (
            <ul className="py-1">
              {activeProjects.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-base-200 transition-colors"
                    onClick={() => {
                      setOpen(false);
                      navigate(`/workspace/${p.id}?tab=terminal&t=${Date.now()}`);
                    }}
                  >
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.7)] shrink-0" />
                    <span className="text-xs truncate">{p.name}</span>
                    <span className="ml-auto text-[10px] text-base-content/50 truncate max-w-[40%]">{p.root_path.split(/[/\\]/).slice(-2).join('/')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
